import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient/supabaseClient";
import { useCart } from "../hooks/useCart"; // Import useCart
import { FaUser, FaLock, FaCreditCard, FaTrash, FaSync, FaExclamationCircle } from "react-icons/fa";
import './Profile.css';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [subscriptions, setSubscriptions] = useState([]);

    // Cart Hook
    const { addToCart, clearCart } = useCart();

    // Profile Data
    const [email, setEmail] = useState("");

    // Password Update
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const navigate = useNavigate();

    const fetchUserData = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                navigate("/Uyegirisi");
                return;
            }

            setUser(user);
            setEmail(user.email);

            // Fetch All Active Subscriptions with Service Details
            const { data: subs } = await supabase
                .from('subscriptions')
                .select('*, services(*)') // Select all service details
                .eq('user_id', user.id)
                .eq('status', 'active')
                .gt('end_date', new Date().toISOString())
                .order('end_date', { ascending: false });

            if (subs) {
                setSubscriptions(subs);
            }

        } catch (error) {
            console.error("Error fetching user data:", error);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setMessage({ type: "", text: "" });

        if (password !== confirmPassword) {
            setMessage({ type: "danger", text: "Şifreler eşleşmiyor." });
            setUpdating(false);
            return;
        }

        if (password.length < 6) {
            setMessage({ type: "danger", text: "Şifre en az 6 karakter olmalı." });
            setUpdating(false);
            return;
        }

        try {
            const { error } = await supabase.auth.updateUser({ password: password });
            if (error) throw error;

            setMessage({ type: "success", text: "Şifre başarıyla güncellendi." });
            setPassword("");
            setConfirmPassword("");
        } catch (error) {
            setMessage({ type: "danger", text: "Şifre güncelleme hatası: " + error.message });
        } finally {
            setUpdating(false);
        }
    };

    const handleRenew = (sub) => {
        if (!sub.services) return;

        // 1. Clear existing cart
        clearCart();

        // 2. Add this specific package to cart
        const productToAdd = {
            id: sub.services.id, // service_id
            name: sub.services.name,
            full_name: sub.services.full_name,
            price: sub.services.price,
            description: sub.services.description,
        };
        addToCart(productToAdd);

        // 3. Redirect to Payment Step
        navigate("/SatinAl", { state: { step: 2 } });
    };

    const handleCancel = async (subId) => {
        if (window.confirm("Üyelik iptal talebiniz ekibimize iletilecektir. Emin misiniz?")) {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'https://staibilisim.com';
                const response = await fetch(`${apiUrl}/api/auth/cancel-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subId, userId: user.id })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'İptal işlemi başarısız.');
                }

                // Refresh list not needed as status doesn't change immediately
                setMessage({ type: "success", text: "İptal talebiniz başarıyla iletildi." });
            } catch (error) {
                console.error("Error cancelling subscription:", error);
                setMessage({ type: "danger", text: "İşlem başarısız oldu." });
            }
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getRemainingDays = (endDate) => {
        return Math.max(0, Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24)));
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="profile-dashboard">
            <Container className="dashboard-container">
                <h2 className="mb-4 fw-bold text-dark">Hesabım</h2>

                {message.text && (
                    <Alert variant={message.type} onClose={() => setMessage({ type: "", text: "" })} dismissible>
                        {message.text}
                    </Alert>
                )}

                <Row className="g-4">
                    {/* LEFT COLUMN: User Settings */}
                    <Col lg={5}>
                        {/* Profile Info */}
                        <div className="dashboard-card">
                            <div className="dashboard-header">
                                <h5 className="dashboard-title"><FaUser className="me-2 text-primary" /> Profil Bilgileri</h5>
                            </div>
                            <div className="dashboard-body">
                                <Form>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="small fw-bold text-muted">E-POSTA ADRESİ</Form.Label>
                                        <Form.Control type="email" value={email} disabled className="profile-input" />
                                    </Form.Group>
                                </Form>
                            </div>
                        </div>

                        {/* Password Change */}
                        <div className="dashboard-card">
                            <div className="dashboard-header">
                                <h5 className="dashboard-title"><FaLock className="me-2 text-primary" /> Güvenlik</h5>
                            </div>
                            <div className="dashboard-body">
                                <Form onSubmit={handleUpdatePassword}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold text-muted">YENİ ŞİFRE</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••"
                                            className="profile-input"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold text-muted">YENİ ŞİFRE (TEKRAR)</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••"
                                            className="profile-input"
                                        />
                                    </Form.Group>

                                    <Button type="submit" variant="dark" className="w-100 py-2 fw-bold" disabled={updating}>
                                        {updating ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                                    </Button>
                                </Form>
                            </div>
                        </div>
                    </Col>

                    {/* RIGHT COLUMN: Subscriptions */}
                    <Col lg={7}>
                        <div className="dashboard-card h-100">
                            <div className="dashboard-header">
                                <h5 className="dashboard-title"><FaCreditCard className="me-2 text-success" /> Aktif Üyeliklerim</h5>
                            </div>
                            <div className="dashboard-body">
                                {subscriptions.length > 0 ? (
                                    <div className="d-flex flex-column">
                                        {subscriptions.map((sub, index) => {
                                            const remainingDays = getRemainingDays(sub.end_date);
                                            return (
                                                <div key={sub.id || index} className="sub-card">
                                                    <div className="sub-header">
                                                        <span className="sub-name">{sub.services?.name || "Paket"}</span>
                                                        <span className="sub-status">AKTİF</span>
                                                    </div>

                                                    <table className="sub-details-table">
                                                        <tbody>
                                                            <tr>
                                                                <td className="label-text">Başlangıç Tarihi</td>
                                                                <td className="value-text">{formatDate(sub.start_date)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="label-text">Bitiş Tarihi</td>
                                                                <td className="value-text">{formatDate(sub.end_date)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="label-text">Kalan Gün</td>
                                                                <td className="value-text remain-days">{remainingDays} Gün</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                    <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top border-light">
                                                        {remainingDays < 30 && sub.services?.id !== 6 && (
                                                            <button
                                                                className="action-btn btn-renew"
                                                                onClick={() => handleRenew(sub)}
                                                            >
                                                                <FaSync className="me-1" /> Süre Uzat
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn btn-cancel"
                                                            onClick={() => handleCancel(sub.id)}
                                                        >
                                                            <FaTrash className="me-1" /> İptal Et
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <FaExclamationCircle className="empty-icon text-muted" />
                                        <h5 className="fw-bold text-dark">Aktif Üyelik Bulunamadı</h5>
                                        <p className="small text-muted mb-0">Hizmetlerimizden yararlanmak için paketlerimizi inceleyin.</p>
                                        <a href="/SatinAl" className="explore-btn">
                                            Paketleri İncele
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Profile;
